import { format, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export const getAbbreviatedTimeZone = (timeZone: string) => {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find((part) => part.type === "timeZoneName");
    return timeZonePart?.value || "Unknown";
  } catch (error) {
    return "Unknown";
  }
};

export const getAbbreviatedUserTimeZone = () => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return getAbbreviatedTimeZone(userTimeZone);
};

/**
 * Formats a date and time string in ISO 8601 format to the standard clinical format
 * of MM/dd/yyyy hh:mm aaa zzz (e.g. 01/01/2022 12:00 pm CST).
 * @param dateTime A string representing a date and time in ISO 8601 format.
 * @returns A formatted string representing the date and time in the user's time zone.
 */
export const formatDateTime = (dateTime: string | undefined) => {
  const date = dateTime ? new Date(dateTime) : new Date();
  return `${format(date, "MM/dd/yyyy")} ${format(date, "hh:mm aaa")} ${getAbbreviatedUserTimeZone()}`;
};

/**
 * Formats a date string in ISO 8601 format to the standard clinical format
 * of MM/dd/yyyy (e.g. 01/01/2022).
 * @param date A string representing a date in ISO 8601 format.
 * @returns A formatted string representing the date in the user's time zone.
 */
export const formatDate = (date: string | undefined) => {
  const dateObj = date ? new Date(date) : new Date();
  return format(dateObj, "MM/dd/yyyy");
};

export const formatUtcDateTime = (dateTime: string) => {
  const utcDate = new Date(dateTime).setUTCHours(12, 0, 0, 0);
  return new Date(utcDate).toISOString();
};

/**
 * Formats a date to YYYY-MM-DD in UTC timezone.
 * This replaces the old moment(date).utc().format("YYYY-MM-DD") behavior.
 * @param date The date to format
 * @returns A string in YYYY-MM-DD format in UTC timezone
 */
export const formatDateUTC = (date: Date) => {
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd");
};

export const toISODateOnly = (date?: Date) => {
  return date?.toISOString().split("T")[0];
};

/**
 * Formats a date to YYYY-MM-DD in the local timezone.
 * Use when the date should reflect the user's calendar day.
 */
export const toISODateOnlyLocal = (date?: Date) => {
  return date ? format(date, "yyyy-MM-dd") : undefined;
};

export const formatTime = (date: string | number) => {
  return new Intl.DateTimeFormat("en-US", {
    timeStyle: "short",
  })
    .format(new Date(date))
    .toLowerCase();
};

export const formatTimeWithTimeZone = (date: string | number) => {
  return `${formatTime(date)} ${getAbbreviatedUserTimeZone()}`;
};

export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = format(parseISO(startDate), "MM/dd/yyyy");
  if (startDate === endDate) return start;
  return `${start} - ${format(parseISO(endDate), "MM/dd/yyyy")}`;
};

/**
 * Formats an elapsed duration in seconds as a stopwatch-style string.
 * - Under 1 hour: M:SS (e.g. "7:05", "0:42")
 * - 1 hour or more: H:MM:SS (e.g. "1:23:45")
 * - Negatives (clock skew guard) are clamped to 0 → "0:00"
 *
 * @param totalSeconds Total elapsed seconds (clamped to 0 if negative).
 * @returns A formatted elapsed duration string.
 */
export const formatElapsedDuration = (totalSeconds: number): string => {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;

  const ss = String(seconds).padStart(2, "0");

  if (hours >= 1) {
    const mm = String(minutes).padStart(2, "0");
    return `${hours}:${mm}:${ss}`;
  }

  return `${minutes}:${ss}`;
};
